/**
 * V70.27: Admin > 인력 현황 관리 (1차 뼈대)
 *
 * 관리 항목 2가지
 *  A. 인력현황 노출 조직 설정  → `visibleOrgUnitIds` (orgUnits 문서 ID)
 *  B. 인력현황 접근 사용자 관리 → `allowedUserIds` (users 문서 ID = UID)
 *
 * 접근 권한: **Admin 전용** (ADMIN_LIKE_ROLES). 인력 현황 허용 사용자는 이 페이지에 접근할 수 없다.
 *  - 라우트: `ProtectedRoute path="/admin/workforce-overview"` (ROUTE_PERMISSIONS → system.admin)
 *  - 페이지 내부: 아래 `isAdminLike` 재검증
 *  - 서버: `getWorkforceOverviewSettings` / `updateWorkforceOverviewSettings` 가 `checkIsAdminLike` 로 재검증
 *
 * 재사용
 *  - 조직 목록: `useOrgUnits` (조직 SSoT = orgUnits). 조직명 문자열 저장 금지 — ID 만 저장.
 *  - 사용자 선택: `EmployeePickerModal` (기존 검색형 직원 선택 모달, 재직자 기준)
 *
 * 범위 제한 (이번 단계에서 하지 않음)
 *  - 상위 조직 선택 시 하위 조직 자동 포함 → **구현하지 않음.** 명시적으로 선택한 조직만 저장한다.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { Users, Building2, Save, Trash2, Plus, Loader2, Search } from 'lucide-react';
import { functions } from '../../firebase/config';
import { useAuthorization } from '../../shared/hooks/useAuthorization';
import { useOrgUnits } from '../../shared/hooks/useOrgUnits';
import { useEmployees } from '../../shared/hooks/useEmployees';
import { EmployeePickerModal } from '../hr/components/EmployeePickerModal';
import { invalidateWorkforceOverviewAccessCache } from '../../shared/hooks/useWorkforceOverviewAccess';

interface SettingsResponse {
  success: boolean;
  visibleOrgUnitIds: string[];
  allowedUserIds: string[];
  updatedAt: string | null;
  updatedBy: string | null;
}

export function AdminWorkforceOverviewSettingsPage() {
  // 라우트 가드(`ROUTE_PERMISSIONS['/admin/workforce-overview'] = 'system.admin'`)와
  // 동일한 권한 키로 페이지 내부에서 한 번 더 검증한다. (기존 `/admin/roles` 선례와 동일)
  const { hasPermission, isLoading: authLoading } = useAuthorization();
  const isAdminLike = hasPermission('system.admin');

  const [visibleOrgUnitIds, setVisibleOrgUnitIds] = useState<string[]>([]);
  const [allowedUserIds, setAllowedUserIds] = useState<string[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
  const [orgSearch, setOrgSearch] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [updatedInfo, setUpdatedInfo] = useState<{ at: string | null; by: string | null }>({ at: null, by: null });

  // 조직 SSoT (orgUnits) — 서버에서 회사 스코프(KR/CN) 필터가 이미 적용된다
  const { orgUnits, isLoading: orgLoading } = useOrgUnits({ isActive: true });
  // 등록된 사용자 표시용 (이름/사번/조직). 등록 후 상태 변경된 사용자도 이름이 보이도록 status='all'
  const { employees, isLoading: empLoading } = useEmployees({ status: 'all', orgUnitId: 'all' });

  const empMap = useMemo(() => {
    const m = new Map<string, { nameKo?: string; employeeNumber?: string; orgUnitName?: string; email?: string }>();
    employees.forEach((e: any) => m.set(e.id, e));
    return m;
  }, [employees]);

  // ── 설정 로드 ──
  useEffect(() => {
    if (authLoading || !isAdminLike || !functions) return;
    let aborted = false;
    (async () => {
      try {
        const fn = httpsCallable<{}, SettingsResponse>(functions!, 'getWorkforceOverviewSettings');
        const r = await fn({});
        if (aborted) return;
        setVisibleOrgUnitIds(r.data.visibleOrgUnitIds || []);
        setAllowedUserIds(r.data.allowedUserIds || []);
        setUpdatedInfo({ at: r.data.updatedAt, by: r.data.updatedBy });
        setLoaded(true);
      } catch (err: any) {
        if (!aborted) setLoadError(err?.message || '설정을 불러오지 못했습니다.');
      }
    })();
    return () => { aborted = true; };
  }, [authLoading, isAdminLike]);

  const toggleOrg = useCallback((orgId: string) => {
    setVisibleOrgUnitIds((prev) => (prev.includes(orgId) ? prev.filter((x) => x !== orgId) : [...prev, orgId]));
    setMessage(null);
  }, []);

  const addUser = useCallback((employeeId: string) => {
    setAllowedUserIds((prev) => (prev.includes(employeeId) ? prev : [...prev, employeeId]));
    setPickerOpen(false);
    setMessage(null);
  }, []);

  const removeUser = useCallback((employeeId: string) => {
    setAllowedUserIds((prev) => prev.filter((x) => x !== employeeId));
    setMessage(null);
  }, []);

  const handleSave = useCallback(async () => {
    if (isSaving || !functions) return;   // 중복 클릭 방지
    setIsSaving(true);
    setMessage(null);
    try {
      const fn = httpsCallable<
        { visibleOrgUnitIds: string[]; allowedUserIds: string[] },
        { success: boolean; visibleOrgUnitIds: string[]; allowedUserIds: string[] }
      >(functions, 'updateWorkforceOverviewSettings');
      const r = await fn({ visibleOrgUnitIds, allowedUserIds });
      setVisibleOrgUnitIds(r.data.visibleOrgUnitIds || []);
      setAllowedUserIds(r.data.allowedUserIds || []);
      // 접근 권한 캐시 무효화 — 부여/회수가 즉시 반영되도록
      invalidateWorkforceOverviewAccessCache();
      setMessage({ type: 'ok', text: '저장되었습니다.' });
    } catch (err: any) {
      setMessage({ type: 'err', text: err?.message || '저장 중 오류가 발생했습니다.' });
    } finally {
      setIsSaving(false);
    }
  }, [isSaving, visibleOrgUnitIds, allowedUserIds]);

  // ── 권한 가드 (페이지 내부 재검증) ──
  if (authLoading) {
    return <div style={{ padding: 40, textAlign: 'center', color: 'var(--color-gray-500)' }}>권한 확인 중...</div>;
  }
  if (!isAdminLike) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
        <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 8 }}>접근 권한이 없습니다</h2>
        <p style={{ color: 'var(--color-gray-600)' }}>인력 현황 관리는 관리자만 이용할 수 있습니다.</p>
      </div>
    );
  }

  const filteredOrgs = orgUnits.filter((o) => {
    if (!orgSearch) return true;
    const q = orgSearch.toLowerCase();
    return (o.name || '').toLowerCase().includes(q) || (o.code || '').toLowerCase().includes(q);
  });

  const sectionStyle: React.CSSProperties = {
    background: 'white', border: '1px solid var(--color-gray-200)', borderRadius: 8,
    padding: 16, marginBottom: 20,
  };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 4 }}>인력 현황 관리</h1>
        <p style={{ fontSize: 13, color: 'var(--color-gray-500)' }}>
          인력 현황 리포트의 대상 조직과 접근 가능 사용자를 관리합니다. (관리자 전용)
        </p>
        {updatedInfo.at && (
          <p style={{ fontSize: 12, color: 'var(--color-gray-400)', marginTop: 4 }}>
            최근 저장: {new Date(updatedInfo.at).toLocaleString('ko-KR')}
          </p>
        )}
      </div>

      {loadError && (
        <div style={{
          padding: '12px 16px', marginBottom: 16, borderRadius: 6,
          background: 'var(--color-red-50)', color: 'var(--color-red-600)', fontSize: 13,
        }}>
          {loadError}
        </div>
      )}

      {/* A. 노출 조직 설정 */}
      <div style={sectionStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <Building2 size={18} color="var(--color-gray-600)" />
          <h2 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>인력현황 노출 조직</h2>
          <span style={{ fontSize: 12, color: 'var(--color-gray-500)' }}>
            선택 {visibleOrgUnitIds.length}개 / 전체 {orgUnits.length}개
          </span>
        </div>
        <p style={{ fontSize: 12, color: 'var(--color-gray-500)', margin: '0 0 12px' }}>
          선택한 조직에 <strong>직접 소속된 직원만</strong> 인력 현황 리포트의 집계 대상이 됩니다.
          각 조직은 독립적으로 선택되며, <strong>상위 조직을 선택해도 하위 조직은 포함되지 않습니다.</strong>
          하위 조직 인원도 보려면 해당 조직을 함께 선택해 주세요.
        </p>

        <div style={{ position: 'relative', marginBottom: 12, maxWidth: 320 }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: 10, color: 'var(--color-gray-400)' }} />
          <input
            value={orgSearch}
            onChange={(e) => setOrgSearch(e.target.value)}
            placeholder="조직명 / 코드 검색"
            style={{
              width: '100%', padding: '8px 10px 8px 30px', fontSize: 13,
              border: '1px solid var(--color-gray-300)', borderRadius: 6,
            }}
          />
        </div>

        {orgLoading || !loaded ? (
          <div style={{ padding: 20, color: 'var(--color-gray-500)', fontSize: 13 }}>조직 목록 로딩 중...</div>
        ) : (
          <div style={{
            maxHeight: 320, overflowY: 'auto',
            border: '1px solid var(--color-gray-100)', borderRadius: 6,
          }}>
            {filteredOrgs.length === 0 ? (
              <div style={{ padding: 20, color: 'var(--color-gray-500)', fontSize: 13 }}>조직이 없습니다.</div>
            ) : filteredOrgs.map((o) => (
              <label
                key={o.id}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '8px 12px', borderBottom: '1px solid var(--color-gray-100)',
                  cursor: 'pointer', fontSize: 13,
                }}
              >
                <input
                  type="checkbox"
                  checked={visibleOrgUnitIds.includes(o.id)}
                  onChange={() => toggleOrg(o.id)}
                  style={{ cursor: 'pointer' }}
                />
                <span style={{ fontWeight: 500, color: 'var(--color-gray-800)' }}>{o.name}</span>
                {o.companyCode && (
                  <span style={{ fontSize: 11, color: 'var(--color-gray-500)' }}>({o.companyCode})</span>
                )}
                {o.code && (
                  <span style={{ fontSize: 11, color: 'var(--color-gray-400)' }}>{o.code}</span>
                )}
              </label>
            ))}
          </div>
        )}
      </div>

      {/* B. 접근 사용자 관리 */}
      <div style={sectionStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <Users size={18} color="var(--color-gray-600)" />
          <h2 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>인력현황 접근 사용자</h2>
          <span style={{ fontSize: 12, color: 'var(--color-gray-500)' }}>{allowedUserIds.length}명</span>
        </div>
        <p style={{ fontSize: 12, color: 'var(--color-gray-500)', margin: '0 0 12px' }}>
          관리자는 등록 없이 접근할 수 있습니다. 여기 등록된 사용자만 추가로 <strong>조직관리 &gt; 인력 현황</strong>에 접근할 수 있으며,
          본 관리 페이지에는 접근할 수 없습니다.
        </p>

        <button
          onClick={() => setPickerOpen(true)}
          disabled={!loaded}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '8px 14px', marginBottom: 12, fontSize: 13,
            background: loaded ? 'var(--color-primary)' : 'var(--color-gray-400)',
            color: 'white', border: 'none', borderRadius: 6,
            cursor: loaded ? 'pointer' : 'not-allowed',
          }}
        >
          <Plus size={14} /> 사용자 추가
        </button>

        {!loaded ? (
          <div style={{ padding: 20, color: 'var(--color-gray-500)', fontSize: 13 }}>로딩 중...</div>
        ) : allowedUserIds.length === 0 ? (
          <div style={{
            padding: 20, color: 'var(--color-gray-500)', fontSize: 13,
            border: '1px dashed var(--color-gray-300)', borderRadius: 6, textAlign: 'center',
          }}>
            등록된 사용자가 없습니다. (관리자만 인력 현황에 접근 가능한 상태)
          </div>
        ) : (
          <div style={{ border: '1px solid var(--color-gray-100)', borderRadius: 6 }}>
            {allowedUserIds.map((uid) => {
              const e = empMap.get(uid);
              return (
                <div
                  key={uid}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '8px 12px', borderBottom: '1px solid var(--color-gray-100)', fontSize: 13,
                  }}
                >
                  <span style={{ fontWeight: 500, minWidth: 90 }}>
                    {e?.nameKo || (empLoading ? '…' : '(조회 불가)')}
                  </span>
                  <span style={{ color: 'var(--color-gray-500)', minWidth: 90 }}>{e?.employeeNumber || '-'}</span>
                  <span style={{ color: 'var(--color-gray-500)', flex: 1 }}>{e?.orgUnitName || '-'}</span>
                  <button
                    onClick={() => removeUser(uid)}
                    title="제거"
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      padding: '4px 10px', fontSize: 12,
                      background: 'white', color: 'var(--color-red-600)',
                      border: '1px solid var(--color-gray-300)', borderRadius: 4, cursor: 'pointer',
                    }}
                  >
                    <Trash2 size={12} /> 제거
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 저장 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          onClick={handleSave}
          disabled={isSaving || !loaded}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '10px 20px', fontSize: 14, fontWeight: 500,
            background: (isSaving || !loaded) ? 'var(--color-gray-400)' : 'var(--color-primary)',
            color: 'white', border: 'none', borderRadius: 6,
            cursor: (isSaving || !loaded) ? 'not-allowed' : 'pointer',
          }}
        >
          {isSaving ? <Loader2 size={14} className="spin" /> : <Save size={14} />}
          {isSaving ? '저장 중...' : '저장'}
        </button>
        {message && (
          <span style={{
            fontSize: 13,
            color: message.type === 'ok' ? 'var(--color-green-600)' : 'var(--color-red-600)',
          }}>
            {message.text}
          </span>
        )}
      </div>

      {/* 사용자 선택 모달 (기존 검색형 컴포넌트 재사용) */}
      <EmployeePickerModal
        isOpen={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={(employeeId) => addUser(employeeId)}
        title="인력 현황 접근 사용자 추가"
      />
    </div>
  );
}

export default AdminWorkforceOverviewSettingsPage;
